<template>
  <div
    class="radial-progress-bar"
    :class="{
      complete: progressPercentage >= 100,
      actionable: isCompleteWithAction,
    }"
    :role="isCompleteWithAction ? 'button' : undefined"
    :tabindex="isCompleteWithAction ? 0 : undefined"
    :aria-label="isCompleteWithAction ? completeAriaLabel : undefined"
    @click="isCompleteWithAction ? $emit('completed') : null"
    @keyup.enter.self="isCompleteWithAction ? $emit('completed') : null"
  >
    <div
      class="progress-bar"
      role="progressbar"
      :aria-valuemin="min"
      :aria-valuemax="max"
      :aria-valuenow="progress"
      :aria-label="progressAriaLabel"
    >
      <svg viewBox="-50,-50,100,100">
        <defs>
          <linearGradient :id="gradientId" x1="0%" y1="0%" x2="120%" y2="100%">
            <stop offset="0%" :style="`stop-color: ${GRADIENT_PURPLE}; stop-opacity: 1`" />
            <stop offset="100%" :style="`stop-color: ${GRADIENT_TEAL}; stop-opacity: 1`" />
          </linearGradient>
        </defs>
        <circle class="outer-ring" :r="radius" shape-rendering="geometricPrecision" />
        <path
          class="progress-ring"
          :d="path"
          shape-rendering="geometricPrecision"
          :stroke="pathStrokeColor"
        />
      </svg>
    </div>
    <div v-if="icon" class="progress-icon">
      <i class="material-symbols-outlined fill" aria-hidden="true">redeem</i>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Radial progress bar using svgs. Has optional complete action when at / over 100%.
 * [x] - accessibility (roles, label by, etc)
 * [x] - stroke toggle at 100% to gradient
 * [x] - stroke fallback to brand blue when in progress
 * [x] - click event on 100% or more
 * [x] - function out keyframe animation
 * [x] - function out colors
 */
import { computed } from 'vue'
import { BRAND_BLUE_100, GRADIENT_TEAL, GRADIENT_PURPLE } from '@/utils/charts'

/** PROPS **/
const props = withDefaults(
  defineProps<{
    radialId: string
    icon?: string
    min: number
    max: number
    progress: number
    progressAriaLabel: string
    completeAriaLabel: string
    radius: number
    completeAction?: boolean
  }>(),
  {
    icon: '',
    radius: 40,
    completeAction: false,
  },
)

/** EMITS **/
defineEmits(['completed'])

/** COMPUTED **/
/** Each gradient must be unique */
const gradientId = computed(() => `progress-gradient-${props.radialId}`)

const isCompleteWithAction = computed(() => props.completeAction && progressPercentage.value >= 100)

/** Toggle between solid color and gradient */
const pathStrokeColor = computed(() => {
  return progressPercentage.value >= 100 ? `url(#${gradientId.value})` : BRAND_BLUE_100
})

/** Calculate progress as a % */
const progressPercentage = computed(() => {
  const actualPct = Math.round(((props.progress - props.min) / (props.max - props.min)) * 100)

  // once over 95, the rounded edges of the path circle get really close and
  // almost look complete, so stop at 95% to avoid confusion
  if (actualPct >= 95 && actualPct < 100) {
    return 95
  }

  return actualPct
})

/** Convert progressPercentage to an angle in radians */
const thetaRadians = computed(() => {
  // convert the progress percentage to an angle in degrees by multiplying by 360 degrees
  const theta = (progressPercentage.value / 100) * 360

  // convert the angle from degrees to radians
  return (theta * Math.PI) / 180
})

/**
 * Calculate the x-coordinate of the end point of the progress arc.
 * Use the cosine function to find the x-coordinate on the circle
 */
const endX = computed(() => {
  return Math.round(props.radius * Math.cos(thetaRadians.value - Math.PI / 2))
})

/**
 * Calculate the y-coordinate of the end point of the progress arc.
 * Use the sin function to find the y-coordinate on the circle
 */
const endY = computed(() => {
  return Math.round(props.radius * Math.sin(thetaRadians.value - Math.PI / 2))
})

/** Generate the SVG path data for the progress arc */
const path = computed(() => {
  // determines if the arc should be greater than 180 degrees (1) or not (0)
  // const largeArcFlag = (progressPercentage.value % 100) > 50 ? 1 : 0

  // cap % to 100
  const cappedPct = Math.min(progressPercentage.value, 100)
  const largeArcFlag = cappedPct >= 50 ? 1 : 0

  // if 100% (or more), complete the circle
  if (cappedPct >= 100) {
    return `M 0 -${props.radius} A ${props.radius} ${props.radius} 0 1 1 0 ${props.radius} A ${props.radius} ${props.radius} 0 1 1 0 -${props.radius}`
  }

  // svg path syntax to draw an arc from the top of the circle (0, -radius) to the calculated end point (endX, endY)
  return `M 0 -${props.radius} A ${props.radius} ${props.radius} 0 ${largeArcFlag} 1 ${endX.value} ${endY.value}`
})
</script>
